import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

const RELOAD_KEY = 'chunk_reload';

/**
 * Wraps React.lazy with automatic page reload on stale chunk errors.
 *
 * After a deployment, users with cached index.html may try to load chunks
 * that no longer exist (new content hashes). This catches the import failure
 * and reloads the page once to pick up the new entry point.
 *
 * The reload flag is cleared on successful navigation, allowing future
 * deploys to trigger another reload if needed.
 */
export function lazyWithRetry<T extends { default: ComponentType<any> }>(
  importFn: () => Promise<T>,
): LazyExoticComponent<T['default']> {
  return lazy(() =>
    importFn()
      .then((module) => {
        // Successful load — clear the reload flag so future deploys can retry
        sessionStorage.removeItem(RELOAD_KEY);
        return module;
      })
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : String(error);
        const isChunkError =
          message.includes('Failed to fetch dynamically imported module') ||
          message.includes('Loading chunk') ||
          message.includes('Loading CSS chunk');

        if (isChunkError) {
          const hasReloaded = sessionStorage.getItem(RELOAD_KEY);
          if (!hasReloaded) {
            sessionStorage.setItem(RELOAD_KEY, '1');
            window.location.reload();
          }
        }

        throw error;
      }) as Promise<T>,
  );
}
